import React from 'react';
import { motion } from 'framer-motion';
import { Lock, Database, Shield, Cookie, UserCog, Mail, Eye, Key } from 'lucide-react';
import SEO from '@/components/SEO';

const Confidentialite = () => {
    return (
        <>
            <SEO 
                title="Politique de Confidentialité"
                description="Découvrez comment Orcsys protège vos données personnelles. Informations sur la collecte, l'utilisation et la protection de vos données."
                keywords="politique de confidentialité, protection des données, RGPD, données personnelles, sécurité des données"
                type="legal"
            />
            <div className="min-h-screen bg-gradient-to-br from-slate-50 to-white pt-24">
                <div className="container px-4 mx-auto py-12">
                    <div className="max-w-4xl mx-auto">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.5 }}
                            className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 md:p-12"
                        >
                            <div className="flex items-center gap-3 mb-8">
                                <Lock className="w-8 h-8 text-orcsys-500" />
                                <h1 className="text-4xl font-bold text-slate-900">
                                    Politique de Confidentialité
                                </h1>
                            </div>

                            <div className="prose prose-slate max-w-none">
                                <p className="text-slate-600 mb-8 text-lg">
                                    Chez Orcsys, nous accordons une grande importance à la protection de vos données personnelles. 
                                    Cette politique de confidentialité explique comment nous collectons, utilisons et protégeons vos informations.
                                </p>

                                <section className="mb-12">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Database className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Collecte des données
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            Nous collectons différents types d'informations pour fournir et améliorer nos services :
                                        </p>
                                        <div className="bg-slate-50 rounded-xl p-6 mb-4">
                                            <h3 className="text-lg font-semibold text-slate-800 mb-3">Données personnelles :</h3>
                                            <ul className="list-none p-0 m-0 space-y-3 text-slate-600">
                                                <li className="flex items-start gap-2">
                                                    <span className="font-semibold min-w-[180px]">Informations de contact :</span>
                                                    <span>Nom, prénom, adresse email, numéro de téléphone</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="font-semibold min-w-[180px]">Informations professionnelles :</span>
                                                    <span>Société, fonction, secteur d'activité</span>
                                                </li>
                                                <li className="flex items-start gap-2">
                                                    <span className="font-semibold min-w-[180px]">Données de connexion :</span>
                                                    <span>Adresse IP, type de navigateur, pages visitées</span>
                                                </li>
                                            </ul>
                                        </div>
                                        <div className="bg-slate-50 rounded-xl p-6">
                                            <h3 className="text-lg font-semibold text-slate-800 mb-3">Méthodes de collecte :</h3>
                                            <ul className="list-none p-0 m-0 space-y-3 text-slate-600">
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Formulaires de contact et d'inscription</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Cookies et technologies similaires</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Interactions avec nos services</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </section>

                                <section className="mb-12">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Eye className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Utilisation des données
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            Vos données sont utilisées aux fins suivantes :
                                        </p>
                                        <div className="bg-slate-50 rounded-xl p-6">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <h3 className="text-lg font-semibold text-slate-800 mb-3">Services</h3>
                                                    <ul className="list-none p-0 m-0 space-y-3 text-slate-600">
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Fournir et personnaliser nos services</span>
                                                        </li>
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Gérer votre compte et vos préférences</span>
                                                        </li>
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Assurer le support technique</span>
                                                        </li>
                                                    </ul>
                                                </div>
                                                <div>
                                                    <h3 className="text-lg font-semibold text-slate-800 mb-3">Communication</h3>
                                                    <ul className="list-none p-0 m-0 space-y-3 text-slate-600">
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Vous informer des mises à jour</span>
                                                        </li>
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Répondre à vos demandes</span>
                                                        </li>
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Envoyer des newsletters (avec consentement)</span>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section className="mb-12">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Shield className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Protection des données
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            Nous mettons en œuvre des mesures de sécurité techniques et organisationnelles pour protéger vos données :
                                        </p>
                                        <div className="bg-slate-50 rounded-xl p-6">
                                            <ul className="list-none p-0 m-0 space-y-4 text-slate-600">
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Chiffrement SSL pour toutes les transmissions de données</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Accès restreint aux données personnelles</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Surveillance continue de nos systèmes</span>
                                                </li>
                                                <li className="flex items-start gap-3">
                                                    <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                    <span>Formation régulière de notre personnel à la sécurité</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </section>

                                <section className="mb-12">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Cookie className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Cookies et technologies similaires
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            Notre site utilise des cookies et technologies similaires pour améliorer votre expérience de navigation. Un cookie est un petit fichier texte stocké sur votre appareil qui nous aide à faire fonctionner notre site.
                                        </p>
                                        <div className="bg-slate-50 rounded-xl p-6 mb-6">
                                            <h3 className="text-lg font-semibold text-slate-800 mb-4">Types de cookies utilisés :</h3>
                                            <div className="space-y-6">
                                                <div>
                                                    <h4 className="font-semibold text-slate-700 mb-2">Cookies essentiels</h4>
                                                    <p className="text-slate-600 mb-2">
                                                        Ces cookies sont nécessaires au fonctionnement du site. Ils ne peuvent pas être désactivés.
                                                    </p>
                                                    <ul className="list-none p-0 m-0 space-y-2 text-slate-600">
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Cookie de consentement (orcsys-cookie-consent)</span>
                                                        </li>
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Cookies de session pour la sécurité</span>
                                                        </li>
                                                    </ul>
                                                </div>

                                                <div>
                                                    <h4 className="font-semibold text-slate-700 mb-2">Cookies analytiques</h4>
                                                    <p className="text-slate-600 mb-2">
                                                        Ces cookies nous permettent d'analyser l'utilisation du site pour en améliorer les performances et les fonctionnalités.
                                                    </p>
                                                    <ul className="list-none p-0 m-0 space-y-2 text-slate-600">
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Google Analytics (mesure d'audience)</span>
                                                        </li>
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Cookies de performance et de débogage</span>
                                                        </li>
                                                    </ul>
                                                </div>

                                                <div>
                                                    <h4 className="font-semibold text-slate-700 mb-2">Cookies de préférences</h4>
                                                    <p className="text-slate-600 mb-2">
                                                        Ces cookies permettent de mémoriser vos choix et préférences pour une meilleure expérience.
                                                    </p>
                                                    <ul className="list-none p-0 m-0 space-y-2 text-slate-600">
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Préférences de langue</span>
                                                        </li>
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Paramètres d'affichage</span>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="bg-slate-50 rounded-xl p-6">
                                            <h3 className="text-lg font-semibold text-slate-800 mb-4">Gestion des cookies</h3>
                                            <div className="space-y-4">
                                                <p className="text-slate-600">
                                                    Vous pouvez contrôler et/ou supprimer des cookies comme vous le souhaitez. Vous avez plusieurs options :
                                                </p>
                                                <ul className="list-none p-0 m-0 space-y-3 text-slate-600">
                                                    <li className="flex items-start gap-3">
                                                        <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                        <span>Utiliser notre bannière de consentement pour gérer vos préférences</span>
                                                    </li>
                                                    <li className="flex items-start gap-3">
                                                        <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                        <span>Modifier les paramètres de votre navigateur pour bloquer ou supprimer les cookies</span>
                                                    </li>
                                                    <li className="flex items-start gap-3">
                                                        <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                        <span>Visiter <a href="https://www.aboutcookies.org" target="_blank" rel="noopener noreferrer" className="text-orcsys-500 hover:text-orcsys-600">www.aboutcookies.org</a> pour plus d'informations sur la gestion des cookies</span>
                                                    </li>
                                                </ul>
                                                <p className="text-slate-600 mt-4">
                                                    Veuillez noter que la restriction des cookies peut affecter certaines fonctionnalités de notre site.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section className="mb-12">
                                    <div className="flex items-center gap-3 mb-4">
                                        <Key className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Vos droits
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            Conformément au RGPD, vous disposez des droits suivants concernant vos données :
                                        </p>
                                        <div className="bg-slate-50 rounded-xl p-6">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                <div>
                                                    <ul className="list-none p-0 m-0 space-y-3 text-slate-600">
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Droit d'accès à vos données</span>
                                                        </li>
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Droit de rectification</span>
                                                        </li>
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Droit à l'effacement</span>
                                                        </li>
                                                    </ul>
                                                </div>
                                                <div>
                                                    <ul className="list-none p-0 m-0 space-y-3 text-slate-600">
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Droit à la limitation du traitement</span>
                                                        </li>
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Droit à la portabilité des données</span>
                                                        </li>
                                                        <li className="flex items-start gap-3">
                                                            <div className="w-2 h-2 rounded-full bg-orcsys-500 mt-2"></div>
                                                            <span>Droit d'opposition</span>
                                                        </li>
                                                    </ul>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </section>

                                <section>
                                    <div className="flex items-center gap-3 mb-4">
                                        <Mail className="w-6 h-6 text-orcsys-500 flex-shrink-0" />
                                        <h2 className="text-2xl font-semibold text-slate-800 m-0">
                                            Contact
                                        </h2>
                                    </div>
                                    <div className="pl-9">
                                        <p className="text-slate-600 mb-4">
                                            Pour toute question concernant notre politique de confidentialité ou pour exercer vos droits :
                                        </p>
                                        <div className="bg-slate-50 rounded-xl p-6">
                                            <ul className="list-none p-0 m-0 space-y-3 text-slate-600">
                                                <li className="flex items-center gap-2">
                                                    <span className="font-semibold min-w-[140px]">Email :</span>
                                                    <a href="mailto:privacy@orcsys.fr" className="text-orcsys-500 hover:text-orcsys-600">
                                                        privacy@orcsys.fr
                                                    </a>
                                                </li>
                                                <li className="flex items-center gap-2">
                                                    <span className="font-semibold min-w-[140px]">Délai de réponse :</span>
                                                    <span>Sous 30 jours maximum</span>
                                                </li>
                                            </ul>
                                        </div>
                                    </div>
                                </section>
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Confidentialite; 